import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api.js";
import { useVisiblePoll } from "../lib/useVisiblePoll.js";
import { DETAIL_QUOTE_POLL_MS } from "../lib/pollConfig.js";
import PriceChart from "../components/PriceChart.jsx";

// Detail view — sticky header + technical lean gauge + AI levels read + range
// bars + fixed-order levels stack + news. Pasted verbatim from
// stock-detail-mockup.jsx (387 lines, approved) with these targeted swaps:
//   - const DATA sample block → useState + useEffect fetching api.detail(sym)
//                               once per mount+symbol change. All 4 timeframes
//                               come in one payload; the tf toggle switches
//                               client-side without re-fetching.
//   - narrow useEffect listener → Tailwind xs: (430px) — indicator grid
//                                 goes 1 → 2 col, "sell/buy" ↔ "strong sell/buy"
//   - hardcoded news array in JSX → d.news mapped through the same shape
//   - onBack prop → wired to a "← back" button in the sticky header
//   - loading + error UI added (mockup didn't include one)

const C = {
  bg: "#0A1428",
  panel: "#132038",
  border: "#1F3355",
  divider: "#16273F",
  chip: "#0E1A30",
  track: "#1B2C48",
  text: "#D5DEE9",
  mute: "#8194AE",
  dim: "#54709A",
  green: "#26D07C",
  red: "#FF5C5C",
  amber: "#E8B339",
};

const TF_ORDER = ["5m", "15m", "1h", "1D"];
const READ_COLOR = { bull: C.green, bear: C.red, neutral: "#8A94A3" };

function scoreLabel(s) {
  if (s <= -60) return "STRONG SELL";
  if (s <= -20) return "SELL";
  if (s < 20) return "CONSOLIDATING";
  if (s < 60) return "BUY";
  return "STRONG BUY";
}
function scoreColor(s) {
  if (s <= -20) return C.red;
  if (s < 20) return C.amber;
  return C.green;
}

/** Amber back-to-watchlist button. Used verbatim in the loading, error,
 *  and main render paths so the affordance is identical everywhere. */
function BackButton({ onBack }) {
  return (
    <button
      onClick={onBack}
      aria-label="Back to watchlist"
      style={{
        display:        "inline-flex",
        alignItems:     "center",
        gap:            6,
        background:     "transparent",
        border:         `1.5px solid ${C.amber}`,
        borderRadius:   8,
        color:          C.amber,
        cursor:         "pointer",
        fontFamily:     "inherit",
        fontSize:       15,
        fontWeight:     700,
        lineHeight:     1,
        padding:        "8px 14px",
        letterSpacing:  0.3,
      }}
    >
      <span style={{ fontSize: 18, lineHeight: 1 }} aria-hidden="true">←</span>
      <span>Back</span>
    </button>
  );
}

function Panel({ children, style }) {
  return (
    <div
      style={{
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        background: C.panel,
        padding: 16,
        marginBottom: 16,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Eyebrow({ children, color }) {
  return (
    <span style={{ fontSize: 10, letterSpacing: 1, textTransform: "uppercase", color: color || C.mute }}>
      {children}
    </span>
  );
}

function RangeBar({ label, low, high, price, big }) {
  const safeLow = Number.isFinite(low) ? low : 0;
  const safeHigh = Number.isFinite(high) ? high : 0;
  const pct = safeHigh > safeLow
    ? Math.max(0, Math.min(100, ((price - safeLow) / (safeHigh - safeLow)) * 100))
    : 50;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 10, letterSpacing: 1, textTransform: "uppercase", color: C.mute }}>{label}</span>
        <span style={{ fontSize: 11, color: C.mute, fontVariantNumeric: "tabular-nums" }}>
          {safeLow.toFixed(2)} – {safeHigh.toFixed(2)}
        </span>
      </div>
      <div style={{ position: "relative", height: big ? 10 : 8, background: C.track, borderRadius: 999 }}>
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: `${pct}%`,
            transform: "translate(-50%,-50%)",
            width: 13,
            height: 13,
            borderRadius: 999,
            border: `2px solid ${C.bg}`,
            background: big ? C.amber : C.text,
          }}
        />
      </div>
    </div>
  );
}

// Read the timeframe from the URL search params (?tf=5m). Falls back to
// "15m" when absent or invalid. Runs synchronously in useState init so a
// hard reload of /stock/IREN?tf=5m renders on 5m from the first paint.
function readTfFromUrl() {
  try {
    const raw = new URLSearchParams(window.location.search).get("tf");
    return TF_ORDER.includes(raw) ? raw : "15m";
  } catch { return "15m"; }
}

export default function DetailPage({ symbol, onBack }) {
  // Timeframe is persisted in the URL as ?tf=5m so refreshes, shared
  // links, and browser-back-then-forward all restore the same view.
  // Sync via history.replaceState — tf changes don't push new history
  // entries (otherwise every pill tap would be a browser-back stop).
  const [tf, setTfState] = useState(readTfFromUrl);
  const setTf = useCallback((newTf) => {
    if (!TF_ORDER.includes(newTf)) return;
    setTfState(newTf);
    try {
      const url = new URL(window.location);
      if (newTf === "15m") {
        // Default TF — clean URL rather than ?tf=15m clutter.
        url.searchParams.delete("tf");
      } else {
        url.searchParams.set("tf", newTf);
      }
      window.history.replaceState({}, "", url);
    } catch { /* history APIs unavailable — non-fatal */ }
  }, []);

  // If the user navigates from one detail page to another (openStock),
  // symbol changes but the URL's ?tf= sticks. Re-sync tf from URL on
  // symbol change so the pill highlight matches the URL.
  useEffect(() => {
    const urlTf = readTfFromUrl();
    if (urlTf !== tf) setTfState(urlTf);
    // Intentional: don't listen on tf — only symbol.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  // Indicator grid is always visible — no more collapse/expand toggle per
  // 2026-07-06 spec ("render the indicator grid open at all times, directly
  // under the gauge"). Left the useState/imports pattern intact so a future
  // collapse can be re-added trivially by wiring a new state var.
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setD(null); setErr(null);
    api.detail(symbol).then(data => { if (!cancelled) setD(data); })
       .catch(e => { if (!cancelled) setErr(e.message || "load failed"); });
    return () => { cancelled = true; };
  }, [symbol]);

  /* --- Live quote refresh (top of page only) ---
     Refreshes just the current price + day change on the same visibility-
     aware timer the watchlist uses. Deliberately DOES NOT re-fetch the
     full detail payload — that's an expensive FMP fan-out (daily candles,
     intraday for VWAP, profile, news) and it's assumed stable-enough
     within a single view session. Indicators, ranges, levels, news all
     stay from the initial fetch.

     Hits /quotes (Finnhub-backed, cheap, 60s cache) with just the current
     symbol; falls back silently on any error so a hiccup doesn't clobber
     the header. */
  const pollQuote = useCallback(() => {
    if (!symbol) return;
    api.quotes([symbol]).then(({ quotes }) => {
      const q = quotes && quotes[0];
      if (!q || !Number.isFinite(q.price)) return;
      setD(prev => prev ? {
        ...prev,
        price:        q.price,
        dayChange:    Number.isFinite(prev.dayChange) && Number.isFinite(q.chg) && Number.isFinite(prev.price)
                        ? (q.price - prev.price) + prev.dayChange
                        : prev.dayChange,
        dayChangePct: q.chg,
        // Also refresh the day range if the quote's dayHigh/dayLow beat
        // what we've been showing — extremes only widen, never shrink,
        // during a session.
        ranges: prev.ranges ? {
          ...prev.ranges,
          day: {
            low:  Math.min(prev.ranges.day?.low  ?? q.dayLow  ?? Infinity, q.dayLow  ?? Infinity),
            high: Math.max(prev.ranges.day?.high ?? q.dayHigh ?? -Infinity, q.dayHigh ?? -Infinity),
          },
        } : prev.ranges,
      } : prev);
    }).catch(() => { /* swallow; next tick retries */ });
  }, [symbol]);

  useVisiblePoll(pollQuote, DETAIL_QUOTE_POLL_MS);

  /* Loading & error states — kept minimal so the mockup's actual layout
     dominates once data lands. */
  if (err) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, color: C.text, padding: 24, fontFamily: "ui-monospace, monospace" }}>
        <BackButton onBack={onBack} />
        <div style={{ marginTop: 16, color: C.red }}>Couldn't load {symbol}: {err}</div>
      </div>
    );
  }
  if (!d) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, color: C.text, padding: 24, fontFamily: "ui-monospace, monospace" }}>
        <BackButton onBack={onBack} />
        <div style={{ marginTop: 16, color: C.dim }}>Loading {symbol}…</div>
      </div>
    );
  }

  const g = d.timeframes[tf] || { score: 0, votes: [] };
  const label = scoreLabel(g.score);
  const col = scoreColor(g.score);
  const needlePct = (g.score + 100) / 2;
  const up = (d.dayChange || 0) >= 0;
  const chColor = up ? C.green : C.red;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: C.bg,
        color: C.text,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* No CSS zoom — see index.css. Just widen + mx-auto + responsive
          padding so content is a centered, comfortably-wide column at
          every viewport size. Mobile stays 440px (mockup default). */}
      <div
        className="mx-auto w-full max-w-[440px] sm:max-w-[580px] md:max-w-[780px] lg:max-w-[1020px] xl:max-w-[1180px] 2xl:max-w-[1340px] px-3 sm:px-4 md:px-6 lg:px-10"
        style={{ paddingBottom: 28, boxSizing: "border-box" }}
      >
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            background: C.bg,
            paddingTop: 10,
            paddingBottom: 8,
            marginBottom: 10,
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", minWidth: 0 }}>
            {/* Amber back-to-watchlist button — deliberately prominent so a
                user always sees a clear "Back" affordance top-left, in
                addition to the browser back gesture (which lands on the
                watchlist via the / entry App.jsx seeds under every detail
                view). Shared BackButton component so loading/error/main
                renders stay visually consistent. */}
            <BackButton onBack={onBack} />
            <span style={{ fontSize: 18, fontWeight: 700, color: "#fff", letterSpacing: 0.5 }}>{d.symbol}</span>
            <span style={{ fontSize: 17, fontWeight: 700, color: chColor, fontVariantNumeric: "tabular-nums" }}>
              {Number.isFinite(d.price) ? d.price.toFixed(2) : "—"}
            </span>
            <span style={{ fontSize: 12, color: chColor, fontVariantNumeric: "tabular-nums" }}>
              {up ? "+" : ""}
              {Number.isFinite(d.dayChange) ? d.dayChange.toFixed(2) : "—"} ({up ? "+" : ""}
              {Number.isFinite(d.dayChangePct) ? d.dayChangePct.toFixed(2) : "—"}%)
            </span>
            <span style={{ fontSize: 10, color: C.dim, whiteSpace: "nowrap" }}>{d.name}</span>

            {/* Extended-hours secondary quote line — Yahoo/thinkorswim style.
                Rendered as a full-width child so flex-wrap pushes it below
                the price row. Session label color matches the chart's
                extended-hours band tint (blue for pre-market, purple for
                after-hours) so the header + chart read as one language.
                Server hides this field (null) during RTH and when the
                stream doesn't have the right session bars. */}
            {d.extendedHours && (() => {
              const eh = d.extendedHours;
              const ehUp    = eh.change >= 0;
              const ehColor = ehUp ? C.green : C.red;
              const labelColor = eh.session === "premarket" ? "#7BB2FF" : "#B78BFF";
              return (
                <div style={{
                  width:        "100%",
                  marginTop:    2,
                  display:      "flex",
                  alignItems:   "baseline",
                  gap:          8,
                  flexWrap:     "wrap",
                  fontVariantNumeric: "tabular-nums",
                }}>
                  <span style={{
                    fontSize:       10,
                    letterSpacing:  1,
                    textTransform:  "uppercase",
                    color:          labelColor,
                    fontWeight:     600,
                  }}>
                    {eh.label}{eh.stale ? " · prior" : ""}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                    {eh.price.toFixed(2)}
                  </span>
                  <span style={{ fontSize: 11, color: ehColor }}>
                    {ehUp ? "+" : ""}{eh.change.toFixed(2)} ({ehUp ? "+" : ""}{eh.changePct.toFixed(2)}%)
                  </span>
                </div>
              );
            })()}
          </div>
          <span style={{ fontSize: 8, color: C.dim, whiteSpace: "nowrap", flexShrink: 0 }}>
            {d.data_health && d.data_health.status === "degraded" ? "delayed" : "live"}
          </span>
        </div>

        {/* Data-health banner — separates three cases:
              stale     — cache served a previously-good response after
                          upstream failed. Numbers are real but not fresh.
              degraded  — upstream FMP is unavailable AND no cache to fall
                          back on. Levels/ranges will be empty; quote+news
                          from Finnhub still populate.
              ok        — no banner. */}
        {d.data_health && d.data_health.status === "stale" && (
          <div style={{
            border: `1px solid ${C.amber}`, background: "#1F2C1A",
            color: C.amber, borderRadius: 8, padding: "8px 12px",
            fontSize: 11, marginBottom: 12, lineHeight: 1.5,
          }}>
            ⓘ Showing cached data — market data temporarily unavailable, retrying.
          </div>
        )}
        {d.data_health && d.data_health.status === "degraded" && (
          <div style={{
            border: `1px solid ${C.red}`, background: "#2A1A1F",
            color: C.red, borderRadius: 8, padding: "8px 12px",
            fontSize: 11, marginBottom: 12, lineHeight: 1.5,
          }}>
            ⚠ Market data unavailable — retrying automatically.
            {d.data_health.daily === false    && " (levels + ranges)"}
            {d.data_health.intraday === false && " (intraday)"}
            {d.data_health.profile === false  && " (company info)"}
          </div>
        )}

        <Panel>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
            <Eyebrow>Technical lean</Eyebrow>
            <div style={{ display: "flex", border: `1px solid ${C.border}`, borderRadius: 6, overflow: "hidden", flexShrink: 0 }}>
              {TF_ORDER.map((t) => (
                <button
                  key={t}
                  onClick={() => setTf(t)}
                  style={{
                    padding: "2px 8px",
                    fontSize: 11,
                    border: "none",
                    cursor: "pointer",
                    background: tf === t ? C.amber : "transparent",
                    color: tf === t ? C.bg : C.mute,
                    fontWeight: tf === t ? 700 : 400,
                    fontFamily: "inherit",
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Hide the gauge (needle + labels + score row) on pending
              intraday tabs — g.score is null there and would misread as
              "STRONG BUY" via scoreLabel's default branch. The coming-
              soon panel further down covers the whole intraday-pending
              state; the gauge only makes sense on 1D (real data) or on
              intraday once Schwab is live. */}
          {!((tf === "5m" || tf === "15m" || tf === "1h") && d.data_health && d.data_health.intradayPending) && (
            <>
              <div
                style={{
                  position: "relative",
                  height: 16,
                  borderRadius: 999,
                  overflow: "hidden",
                  marginBottom: 8,
                  background: "linear-gradient(90deg,#FF5C5C 0%,#B4443A 22%,#E8B339 50%,#3E9E63 78%,#26D07C 100%)",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: `calc(${needlePct}% - 2px)`,
                    width: 3,
                    background: "#fff",
                    boxShadow: "0 0 6px rgba(255,255,255,0.9)",
                  }}
                />
              </div>
              {/* Compact/full labels — narrow-JS listener replaced with xs: */}
              <div className="text-[8px] xs:text-[9px] xs:tracking-[1px]"
                   style={{ display: "flex", justifyContent: "space-between", textTransform: "uppercase", color: C.dim, marginBottom: 12, gap: 4 }}>
                <span style={{ whiteSpace: "nowrap" }}><span className="hidden xs:inline">strong </span>sell</span>
                <span style={{ whiteSpace: "nowrap" }}>neutral</span>
                <span style={{ whiteSpace: "nowrap" }}><span className="hidden xs:inline">strong </span>buy</span>
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <span style={{ fontSize: 18, color: col }}>{g.score <= -20 ? "◀" : g.score >= 20 ? "▶" : "◆"}</span>
                <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: 1, color: col }}>{label}</span>
                <span style={{ fontSize: 12, color: C.mute }}>
                  ({g.score > 0 ? "+" : ""}
                  {g.score})
                </span>
              </div>
            </>
          )}

          {(() => {
            const isIntradayTf = tf === "5m" || tf === "15m" || tf === "1h";
            const intradayPending = d.data_health && d.data_health.intradayPending;

            // Intraday feed pending — show a clean "coming soon" panel on
            // 5m/15m/1h instead of the indicator grid + partial signals.
            // Explicitly refuses to render ANY vote so a stray one-signal
            // score can't mislead a trading decision (the IREN class of
            // false-SELL). 1D keeps its FMP daily indicators normally.
            if (isIntradayTf && intradayPending) {
              return (
                <div
                  style={{
                    marginTop:    14,
                    padding:      "20px 16px",
                    border:       `1px dashed ${C.border}`,
                    borderRadius: 8,
                    background:   C.chip,
                    textAlign:    "center",
                  }}
                >
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: C.amber, marginBottom: 8 }}>
                    Intraday feed coming soon
                  </div>
                  <div style={{ fontSize: 11, color: C.text, lineHeight: 1.45, marginBottom: 6 }}>
                    {tf} indicators need a live consolidated intraday feed.
                  </div>
                  <div style={{ fontSize: 10, color: C.mute, lineHeight: 1.4 }}>
                    Waiting on Schwab Trader API approval. Full RSI/MACD/VWAP/EMA on 5m, 15m, and 1h will populate here once the feed is wired.
                  </div>
                  <div style={{ fontSize: 9, color: C.dim, marginTop: 10 }}>
                    In the meantime, use the <b style={{ color: C.text }}>1D</b> tab for daily technicals, or the header price + change (Finnhub real-time).
                  </div>
                </div>
              );
            }

            // Normal path — indicator grid + optional data-source badge.
            return (
              <>
                {isIntradayTf && d.data_health && d.data_health.intradayLabel && (
                  <div style={{ marginTop: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                    <span
                      title={d.data_health.intradayRealtime
                        ? "Full consolidated tape, real-time"
                        : "Delayed / non-consolidated intraday source — day-total volume falls back to FMP consolidated."}
                      style={{
                        fontSize:       9,
                        fontWeight:     700,
                        letterSpacing:  0.6,
                        textTransform:  "uppercase",
                        color:          d.data_health.intradayRealtime ? C.green : C.amber,
                        border:         `1px solid ${d.data_health.intradayRealtime ? C.green : C.amber}`,
                        borderRadius:   4,
                        padding:        "2px 6px",
                        background:     "transparent",
                      }}
                    >
                      {d.data_health.intradayLabel}
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-1 xs:grid-cols-2 gap-2" style={{ marginTop: 14 }}>
                  {g.votes.map((v, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, background: C.chip, borderRadius: 6, padding: "6px 8px", minWidth: 0 }}>
                      <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                        <span style={{ fontSize: 11, color: C.text }}>{v.label}</span>
                        <span style={{ fontSize: 9, color: C.dim }}>{v.detail}</span>
                      </div>
                      <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", padding: "2px 6px", borderRadius: 4, color: READ_COLOR[v.read], background: (READ_COLOR[v.read] || "#8A94A3") + "1A", flexShrink: 0, whiteSpace: "nowrap" }}>
                        {v.read}
                      </span>
                    </div>
                  ))}
                  {g.votes.length === 0 && (
                    <div style={{ gridColumn: "1 / -1", padding: 8, textAlign: "center", fontSize: 10, color: C.dim }}>
                      No indicator data for {tf} yet.
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </Panel>

        {/* Price chart — Lightweight Charts candlesticks with six timeframes
            (1D/5D → intraday, 1M/6M/YTD/1Y → daily). Server routes intraday
            through the Schwab-or-FMP dispatcher per active connection.
            Extended-hours bars (pre-market + after-hours) render with muted
            candle colors and translucent background stripes. S/R + visible-
            period HI/LO overlaid as horizontal price lines. */}
        <Panel>
          <div style={{ marginBottom: 10 }}>
            <Eyebrow>Price chart</Eyebrow>
          </div>
          <PriceChart symbol={symbol} />
        </Panel>

        {d.summary && (
          <Panel>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <Eyebrow color={C.amber}>◆ Levels read</Eyebrow>
              <span style={{ fontSize: 9, color: C.dim }}>auto-generated · descriptive only</span>
            </div>
            <p style={{ fontSize: 13, lineHeight: 1.6, color: "#BFC9D6", margin: 0 }}>{d.summary}</p>
          </Panel>
        )}

        <Panel>
          <div style={{ marginBottom: 12 }}>
            <Eyebrow>Where price sits</Eyebrow>
          </div>
          <RangeBar label="1-day range"  low={d.ranges.day.low}     high={d.ranges.day.high}     price={d.price} />
          <RangeBar label="5-day range"  low={d.ranges.fiveDay.low} high={d.ranges.fiveDay.high} price={d.price} />
          <RangeBar label="52-week range" low={d.ranges.week52.low} high={d.ranges.week52.high}  price={d.price} big />
        </Panel>

        <Panel>
          <div style={{ marginBottom: 4 }}>
            <Eyebrow>Key levels</Eyebrow>
          </div>
          {d.levels
            .map((lv, i, arr) => {
              const value = Number.isFinite(lv.value) ? lv.value : null;
              const above = value != null && d.price >= value;
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 0",
                    borderBottom: i === arr.length - 1 ? "none" : `1px solid ${C.divider}`,
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontSize: 13, color: C.text, lineHeight: 1.2 }}>{lv.key}</span>
                    {lv.note && <span style={{ fontSize: 9, color: C.dim, lineHeight: 1.2 }}>{lv.note}</span>}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontSize: 13, color: C.text, fontVariantNumeric: "tabular-nums" }}>{value != null ? value.toFixed(2) : "—"}</span>
                    <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", width: 60, textAlign: "right", color: value == null ? C.dim : above ? C.green : C.red }}>
                      {value == null ? "—" : above ? "▲ above" : "▼ below"}
                    </span>
                  </div>
                </div>
              );
            })}
          <div style={{ fontSize: 9, color: C.dim, marginTop: 8 }}>
            price {Number.isFinite(d.price) ? d.price.toFixed(2) : "—"} — above/below is current price vs each level
          </div>
        </Panel>

        <Panel style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>
            <Eyebrow>Latest news</Eyebrow>
          </div>
          {(d.news || []).map((n, i, arr) => (
            <div key={i} style={{ padding: "8px 0", borderBottom: i === arr.length - 1 ? "none" : `1px solid ${C.divider}` }}>
              <a href={n.url || "#"} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "none" }}>
                <div style={{ fontSize: 12, color: C.text, lineHeight: 1.35 }}>{n.t}</div>
                <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>
                  {n.src} · {n.ago} ago
                </div>
              </a>
            </div>
          ))}
          {(!d.news || d.news.length === 0) && (
            <div style={{ fontSize: 11, color: C.dim, padding: "8px 0" }}>No recent headlines.</div>
          )}
        </Panel>

        <div style={{ textAlign: "center", fontSize: 9, color: C.dim }}>
          Live data · not investment advice · levels read is descriptive, not a price prediction
        </div>
      </div>
    </div>
  );
}
